"use client";

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { validateFields, validateTableData } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { SECTION_TYPES } from "@/constants/admin/sectionTypes";
import { fetchLatestBlogSources } from "@/api/admin/latestBlog";
import { fetchPopularBlogSources } from "@/api/admin/popularBlog";
import { Section, MetaConfig, FormState } from "./types";
import { STATE_SLICE_MAP, INITIAL_FORM_STATE } from "./constants";
import {
  buildDefaultFieldValues,
  extractComponentFields,
  buildMetadataStates,
  injectDynamicOptions,
  getSourceData,
  hasFormChanged,
  normalizeEmptyFields,
} from "./utils";
import { enrichBanner6SourceForForm } from "@/constants/admin/fields/banner/banner_6";
import { BANNER_VARIANTS } from "@/constants/admin/sectionTypes";

/** Persisted header row id (API). Rejects empty, temp_*, non-numeric, and the string "undefined". */
function parseStoredHeaderRowId(value: unknown): number | null {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s || s === "undefined" || s.startsWith("temp_")) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

/** API JSON often sends numeric IDs; admin SELECT option values are strings (Radix expects a match). */
function coerceAdminSelectFieldValues(
  config: Array<{ fieldName?: string; filedType?: string }>,
  fields: Record<string, any>,
): Record<string, any> {
  const next = { ...fields };
  let changed = false;
  for (const field of config) {
    if (field?.filedType !== "SELECT" || !field.fieldName) continue;
    const key = field.fieldName;
    const v = next[key];
    if (v != null && v !== "" && typeof v !== "string") {
      next[key] = String(v);
      changed = true;
    }
  }
  return changed ? next : fields;
}

interface UseForm1Props {
  selectedSection: Section;
  hasMetadata: boolean;
  dynamicOptions: Record<string, any[]>;
  pageId?: string;
  pageComponentId?: string;
  variantDetails?: any; // Passed from parent to avoid circular dependency
}

export const useForm1 = ({
  selectedSection,
  hasMetadata,
  dynamicOptions,
  pageId,
  pageComponentId,
  variantDetails,
}: UseForm1Props) => {
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
  const prevSectionRef = useRef<{ id?: string; componentId?: string; data?: any }>({});
  const latestBlogsSectionKeyRef = useRef("");
  const latestBlogsPrevToggleRef = useRef(false);

  const dispatch = useAppDispatch();
  const currentVariantDetails = variantDetails; // Use prop instead of importing
  const currentUser = useAppSelector((state) => state.user?.user);

  const reduxState = useAppSelector((state) => {
    if (!currentVariantDetails?.selector) return null;
    const getSlice = STATE_SLICE_MAP[selectedSection.type] || STATE_SLICE_MAP.default;
    return currentVariantDetails.selector(getSlice(state));
  });

  const componentFieldsConfig: any[] = currentVariantDetails?.componentFields || [];
  const metadataFieldsArray: MetaConfig[] = (currentVariantDetails as any)?.metadataFields || [];
  const defaultRows: any[] =
    (currentVariantDetails as any)?.defaultRows ||
    ((currentVariantDetails as any)?.defaultRow ? [(currentVariantDetails as any).defaultRow] : []);

  const metadataFieldsWithOptions = useMemo(
    () => injectDynamicOptions(metadataFieldsArray, dynamicOptions),
    [metadataFieldsArray, dynamicOptions]
  );

  const componentFieldsConfigWithOptions = useMemo(() => {
    return componentFieldsConfig.map((field: any) => ({
      ...field,
      ...(field.dynamicOptions &&
      dynamicOptions[field.dynamicOptions] != null && {
        options: dynamicOptions[field.dynamicOptions],
      }),
    }));
  }, [componentFieldsConfig, dynamicOptions]) as typeof componentFieldsConfig;

  // Initialize form state when section changes
  useEffect(() => {
    const { id, componentId, data } = selectedSection;

    if (!currentVariantDetails || !hasFormChanged(prevSectionRef.current, { id, componentId, data }, Object.keys(formState.componentFields).length)) {
      return;
    }

    prevSectionRef.current = { id, componentId, data };
    let sourceData = getSourceData(data, reduxState);
    if (currentVariantDetails?.id === BANNER_VARIANTS.BANNER_6) {
      sourceData = enrichBanner6SourceForForm(sourceData as Record<string, unknown>) as typeof sourceData;
    }
    const defaults = buildDefaultFieldValues(componentFieldsConfig);

    const extracted = extractComponentFields(
      sourceData,
      defaults,
      metadataFieldsArray,
      hasMetadata,
    );

    const componentFieldsCoerced = coerceAdminSelectFieldValues(
      componentFieldsConfig,
      extracted,
    );
    const baseMetadataStates = buildMetadataStates(
      sourceData,
      metadataFieldsArray,
      defaultRows,
      hasMetadata
    );
    const isLatestBlogsHydrated =
      selectedSection.type === SECTION_TYPES.LATEST_BLOGS &&
      metadataFieldsArray.length === 0;
    const metadataStates = isLatestBlogsHydrated
      ? { metadata: Array.isArray(sourceData?.metadata) ? [...sourceData.metadata] : [] }
      : baseMetadataStates;

    setFormState({
      componentFields: extractComponentFields(sourceData, defaults, metadataFieldsArray, hasMetadata),
      metadataStates,
      componentFieldErrors: {},
      componentFieldMessages: {},
      metadataErrors: {},
      metadataMessages: {},
    });

    if (data && currentVariantDetails.setAction) {
      dispatch(
        currentVariantDetails.setAction({
          ...(typeof data === "object" && data !== null && !Array.isArray(data)
            ? data
            : {}),
          ...componentFieldsCoerced,
        } as Parameters<typeof currentVariantDetails.setAction>[0]),
      );
    }
  }, [selectedSection, reduxState, currentVariantDetails, componentFieldsConfig, metadataFieldsArray, defaultRows, hasMetadata, dispatch, formState.componentFields]);

  // Dispatch Redux update helper
  const dispatchUpdate = useCallback(
    (updates: Record<string, any>) => {
      if (!currentVariantDetails?.setAction) return;

      const prev = reduxState as Record<string, any> | null;
      const mergedId =
        updates.id !== undefined && updates.id !== null && String(updates.id) !== ""
          ? String(updates.id)
          : String(prev?.id ?? "");
          
      dispatch(
        currentVariantDetails.setAction({
          ...prev,
          ...updates,
          id: mergedId,
          status: updates.status ?? prev?.status ?? false,
        } as any)
      );
    },
    [currentVariantDetails, dispatch, reduxState]
  );

  const handleComponentFieldsUpdate = useCallback(
    (updatedData: Record<string, any>) => {
      setFormState((prev) => ({ ...prev, componentFields: updatedData }));
      dispatchUpdate({ ...updatedData, ...formState.metadataStates });
    },
    [dispatchUpdate, formState.metadataStates]
  );

  const handleMetadataUpdate = useCallback(
    (dataKey: string, updatedData: any[]) => {
      const newMetadataArray = Array.isArray(updatedData) ? [...updatedData] : [];
      setFormState((prev) => ({
        ...prev,
        metadataStates: { ...prev.metadataStates, [dataKey]: newMetadataArray },
      }));
      dispatchUpdate({ ...formState.componentFields, [dataKey]: newMetadataArray });
    },
    [dispatchUpdate, formState.componentFields]
  );

  useEffect(() => {
    const isLatest = selectedSection.type === SECTION_TYPES.LATEST_BLOGS;
    const isPopular = selectedSection.type === SECTION_TYPES.POPULAR_BLOGS;
    if ((!isLatest && !isPopular) || !pageId) return;

    const sectionKey = `${selectedSection.type}:${selectedSection.id}:${selectedSection.componentId ?? ""}`;
    if (latestBlogsSectionKeyRef.current !== sectionKey) {
      latestBlogsSectionKeyRef.current = sectionKey;
      latestBlogsPrevToggleRef.current = false;
    }

    const toggleOn = Boolean(
      isLatest
        ? formState.componentFields.fetch_latest_blogs
        : formState.componentFields.fetch_popular_blogs
    );
    const prevToggle = latestBlogsPrevToggleRef.current;
    latestBlogsPrevToggleRef.current = toggleOn;

    if (!toggleOn || prevToggle) return;

    let cancelled = false;
    (async () => {
      try {
        const rows = isLatest
          ? await fetchLatestBlogSources(Number(pageId))
          : await fetchPopularBlogSources(Number(pageId));
        if (cancelled) return;
        handleMetadataUpdate("metadata", rows);
        if (rows.length === 0) {
          toast({
            title: "No blog posts found",
            description: isLatest
              ? "Publish up to 30 blog pages for this university to appear here."
              : "Publish up to 15 blog pages for this university to appear here.",
          });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : "Try again.";
          toast({
            title: isLatest ? "Could not load latest blogs" : "Could not load popular blogs",
            description: message,
            variant: "destructive",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedSection.type,
    selectedSection.id,
    selectedSection.componentId,
    formState.componentFields.fetch_latest_blogs,
    formState.componentFields.fetch_popular_blogs,
    pageId,
    handleMetadataUpdate,
  ]);

  const validateForm = useCallback((): boolean => {
    const { errors: fieldErrors, messages: fieldMessages } = validateFields(
      componentFieldsConfigWithOptions,
      formState.componentFields,
    );

    const { metadataErrors, metadataMessages, hasErrors } = metadataFieldsArray.reduce(
      (acc, { dataKey, fields }) => {
        const { errors, messages } = validateTableData(fields || [], formState.metadataStates[dataKey] || []);
        acc.metadataErrors[dataKey] = errors;
        acc.metadataMessages[dataKey] = messages;
        if (Object.keys(errors).length > 0) acc.hasErrors = true;
        return acc;
      },
      { metadataErrors: {}, metadataMessages: {}, hasErrors: false } as any
    );

    setFormState((prev) => ({
      ...prev,
      componentFieldErrors: fieldErrors,
      componentFieldMessages: fieldMessages,
      metadataErrors,
      metadataMessages,
    }));

    return Object.keys(fieldErrors).length === 0 && !hasErrors;
  }, [
    componentFieldsConfigWithOptions,
    formState.componentFields,
    metadataFieldsArray,
    formState.metadataStates,
  ]);

  const handleSave = useCallback(
    async (onSave: (updatedSection: Section) => void) => {
      if (!currentVariantDetails) {
        toast({ title: "Error", description: "Widget configuration not found", variant: "destructive" });
        return;
      }

      // Enforce optional per-group row-count limits (e.g. min 2 / max 10 logos).
      const limitError = metadataFieldsArray
        .map(({ dataKey, minRows, maxRows, title }) => {
          const count = (formState.metadataStates[dataKey] || []).length;
          const label = title || "Items";
          if (minRows != null && count < minRows)
            return `${label}: add at least ${minRows} (currently ${count}).`;
          if (maxRows != null && count > maxRows)
            return `${label}: maximum ${maxRows} allowed (currently ${count}).`;
          return null;
        })
        .find(Boolean);
      if (limitError) {
        toast({ title: "Validation Error", description: limitError, variant: "destructive" });
        return;
      }

      if (!validateForm()) {
        toast({ title: "Validation Error", description: "Please fix the errors before saving", variant: "destructive" });
        return;
      }

      try {
        const { id: _, ...fieldsWithoutId } = normalizeEmptyFields(
          formState.componentFields,
          componentFieldsConfig
        );
        const rawRowId = (reduxState as any)?.id || formState.componentFields.id;
        const rowIdNum = Number(rawRowId);
        const isUpdate =
          rawRowId != null &&
          String(rawRowId) !== "" &&
          String(rawRowId) !== "undefined" &&
          Number.isFinite(rowIdNum) &&
          rowIdNum > 0 &&
          !String(rawRowId).startsWith("temp_");

        let payload = {
          ...fieldsWithoutId,
          ...formState.metadataStates,
          variant: currentVariantDetails.id,
          page_id: pageId ? Number(pageId) : undefined,
          component_id: pageComponentId ? Number(pageComponentId) : undefined,
          ...(isUpdate ? { updated_by: currentUser?.id } : { created_by: currentUser?.id }),
        };

        if (typeof currentVariantDetails.transformPayload === "function") {
          payload = currentVariantDetails.transformPayload(payload);
        }

        let responseId: string;
        if (isUpdate) {
          await currentVariantDetails.updateMethod?.(rowIdNum, payload);
          responseId = String(rowIdNum);
        } else {
          const created = await currentVariantDetails.createMethod?.(payload);
          const newId = created?.data?.id ?? (created as any)?.id;
          if (newId == null || !Number.isFinite(Number(newId)) || Number(newId) <= 0) {
            throw new Error("Failed to get ID from create response");
          }
          responseId = String(newId);
        }

        const metadata2 =
          payload.metadata_2 !== undefined
            ? payload.metadata_2
            : formState.metadataStates.metadata_2;

        dispatchUpdate({
          ...formState.componentFields,
          id: responseId,
          ...formState.metadataStates,
          ...(metadata2 !== undefined ? { metadata_2: metadata2 } : {}),
        });
        setFormState((prev) => ({ ...prev, componentFields: { ...prev.componentFields, id: responseId } }));

        toast({ title: "Success", description: "Widget settings saved successfully" });
        onSave({
          ...selectedSection,
          componentId: responseId,
          data: {
            ...formState.componentFields,
            id: responseId,
            ...formState.metadataStates,
            ...(metadata2 !== undefined ? { metadata_2: metadata2 } : {}),
          },
        });
      } catch (error: any) {
        console.error("Failed to save:", error);
        toast({ title: "Error", description: error.message || "Failed to save widget settings", variant: "destructive" });
      }
    },
    [currentVariantDetails, validateForm, formState, reduxState, pageId, pageComponentId, currentUser?.id, dispatchUpdate, selectedSection, componentFieldsConfig, metadataFieldsArray]
  );

  return {
    formState,
    currentVariantDetails,
    componentFieldsConfig: componentFieldsConfigWithOptions,
    metadataFieldsWithOptions,
    handleComponentFieldsUpdate,
    handleMetadataUpdate,
    handleSave,
  };
};
